import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, map, Observable, takeUntil } from 'rxjs';
import {
  ArrowLeftRight,
  Check,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  LucideAngularModule,
  RefreshCw,
  TriangleAlert,
} from 'lucide-angular';

import { Destroyer } from '@app/shared/abstract/destroyer';
import { RouteParams } from '@app/shared/abstract/route-params';
import { Column } from '@app/partials/datatable/data-table/column';
import { RowAction } from '@app/partials/datatable/data-table/row-action';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { NotificationService } from '@app/shared/notification/notification.service';
import { VenueService } from '@app/venues/shared/venue.service';

import { PaymentRemediationService } from '../shared/payment-remediation.service';
import {
  RemediationCase,
  RemediationListResponse,
  STRAND_LABELS,
} from '../shared/payment-remediation.model';

/**
 * TVG-176: Live payment remediation page.
 *
 * Surfaces, in real time, every confirmed event whose payment schedule looks
 * wrong, and lets finance work the list down to zero. The list runs its
 * criteria live on every load, so resolved cases drop off on their own.
 */
@Component({
  selector: 'app-payment-remediation',
  standalone: true,
  imports: [
    DataTableComponent,
    LucideAngularModule,
    ReactiveFormsModule,
    TextInputComponent,
    SelectComponent,
    ModalComponent,
  ],
  templateUrl: './payment-remediation.component.html',
})
export class PaymentRemediationComponent extends Destroyer implements OnInit {
  protected readonly FixIcon = RefreshCw;
  protected readonly ApproveIcon = Check;
  protected readonly ReassignIcon = ArrowLeftRight;
  protected readonly GhostIcon = TriangleAlert;
  // TVG-207: opens the event's Products & Finances page in a new tab.
  protected readonly ViewFinancesIcon = ExternalLink;
  protected readonly ChevronDown = ChevronDown;
  protected readonly ChevronUp = ChevronUp;
  protected readonly strandLabels = STRAND_LABELS;

  /**
   * TVG-201: whether the plain-language explainer panel is expanded. Collapsed
   * by default so it never takes up space until finance chooses to open it.
   */
  protected isExplainerOpen = false;

  protected response?: RemediationListResponse;
  protected loading = false;
  protected processing = false;

  protected searchControl = new FormControl('');
  protected strandControl = new FormControl<string>('');
  protected venueControl = new FormControl<number | null>(null);

  protected strandChoices = [
    { value: '', label: 'All strands' },
    { value: 'cascade_not_absorbed', label: STRAND_LABELS.cascade_not_absorbed },
    { value: 'hand_edited', label: STRAND_LABELS.hand_edited },
    { value: 'ghost_row', label: STRAND_LABELS.ghost_row },
  ];
  protected venueChoices$: Observable<{ value: any; label: string }[]>;

  protected selectedRowIds = new Set<number>();

  // Reassign modal state.
  protected isReassignOpen = false;
  protected reassignCase: RemediationCase | null = null;
  protected reassignTargetControl = new FormControl<string>('food_drink_50');
  protected reassignChoices = [
    { value: 'reservation_fee', label: 'Reservation Fee' },
    { value: 'venue_hire', label: 'First Payment (Venue Hire)' },
    { value: 'food_drink_50', label: 'Second Payment' },
    { value: 'final_payment_plus_deposit', label: 'Final Payment + Deposit' },
  ];

  routeParams = new RouteParams({
    pageNum: 1,
    pageSize: 25,
    sort: [{ field: 'difference', dir: 'desc' }],
    filter: [],
  });

  tableColumns: Column<RemediationCase>[] = [
    {
      field: 'event_name',
      header: 'Event',
      sortable: true,
      sortSource: 'event',
      callback: (row) => (row.event_reference ? `${row.event_name} (${row.event_reference})` : row.event_name),
    },
    { field: 'venue_name', header: 'Venue' },
    {
      field: 'strand',
      header: 'Strand',
      sortable: true,
      sortSource: 'strand',
      callback: (row) => STRAND_LABELS[row.strand],
    },
    { field: 'stored', header: 'Stored', align: 'right', callback: (row) => this.money(row.stored) },
    { field: 'calculated', header: 'Calculated', align: 'right', callback: (row) => this.money(row.calculated) },
    {
      field: 'difference',
      header: 'Difference',
      align: 'right',
      sortable: true,
      sortSource: 'difference',
      type: 'number',
      callback: (row) => this.money(row.difference),
    },
  ];

  rowActions: RowAction<RemediationCase>[] = [
    {
      icon: this.ViewFinancesIcon,
      callback: (row) => this.openFinances(row),
      classes: 'text-gray-500 hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-500',
      title: () => "Open this event's Products & Finances page in a new tab",
    },
    {
      icon: this.FixIcon,
      callback: (row) => this.fix(row),
      condition: (row) => row.actions.includes('fix'),
      title: () => 'Fix (re-run the calculation and write the corrected schedule)',
    },
    {
      icon: this.ApproveIcon,
      callback: (row) => this.approveOverride(row),
      condition: (row) => row.actions.includes('approve_override'),
      classes: 'text-gray-500 hover:text-success-500 dark:text-gray-400 dark:hover:text-success-500',
      title: () => 'Approve as override (records the stored figure as correct and intended)',
    },
    {
      icon: this.ReassignIcon,
      callback: (row) => this.openReassign(row),
      condition: (row) => row.actions.includes('reassign'),
      classes: 'text-gray-500 hover:text-warning-500 dark:text-gray-400 dark:hover:text-warning-500',
      title: () => 'Reassign ghost-row money onto a standard schedule row',
    },
  ];

  constructor(
    private readonly service: PaymentRemediationService,
    private readonly venueService: VenueService,
    private readonly notifications: NotificationService,
    private readonly router: Router,
  ) {
    super();
    this.venueChoices$ = this.venueService.getAll().pipe(
      map((res) => [
        { value: null, label: 'All venues' },
        ...res.data.map((v) => ({ value: v.id, label: v.name })),
      ]),
    );
  }

  ngOnInit(): void {
    this.loadData();

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());

    this.strandControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());

    this.venueControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyFilters());
  }

  loadData(): void {
    this.loading = true;
    this.service
      .list(this.routeParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.response = res;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.notifications.error('Failed to load the remediation list.');
        },
      });
  }

  private applyFilters(): void {
    this.routeParams.filter = [
      { field: 'search', value: this.searchControl.value ?? '' },
      { field: 'strand', value: this.strandControl.value ?? '' },
      { field: 'venue_id', value: this.venueControl.value ?? '' },
    ];
    this.routeParams.pageNum = 1;
    this.selectedRowIds = new Set<number>();
    this.loadData();
  }

  handleRoute(params: RouteParams): void {
    this.routeParams.sort = params.sort;
    this.routeParams.pageNum = params.pageNum;
    this.routeParams.pageSize = params.pageSize;
    this.loadData();
  }

  onSelectionChange(event: { item: RemediationCase; checked: boolean }): void {
    const id = Number(event.item.id);
    if (event.checked) {
      this.selectedRowIds.add(id);
    } else {
      this.selectedRowIds.delete(id);
    }
    this.selectedRowIds = new Set(this.selectedRowIds);
  }

  protected get selectedCount(): number {
    return this.selectedRowIds.size;
  }

  fix(row: RemediationCase): void {
    this.processing = true;
    this.service.fix(row.event_id).subscribe({
      next: (res) => {
        this.notifications.success(res.message);
        this.afterAction();
      },
      error: (err) => this.handleError(err),
    });
  }

  approveOverride(row: RemediationCase): void {
    this.processing = true;
    this.service.approveOverride(row.event_id).subscribe({
      next: (res) => {
        this.notifications.success(res.message);
        this.afterAction();
      },
      error: (err) => this.handleError(err),
    });
  }

  bulkFix(): void {
    if (this.selectedCount === 0) {
      return;
    }
    this.processing = true;
    this.service.bulkFix([...this.selectedRowIds]).subscribe({
      next: (res) => {
        this.notifications.success(res.message);
        this.afterAction();
      },
      error: (err) => this.handleError(err),
    });
  }

  openReassign(row: RemediationCase): void {
    this.reassignCase = row;
    this.reassignTargetControl.setValue('food_drink_50');
    this.isReassignOpen = true;
  }

  closeReassign(): void {
    if (this.processing) {
      return;
    }
    this.isReassignOpen = false;
    this.reassignCase = null;
  }

  confirmReassign(): void {
    const ghostId = this.reassignCase?.ghost_payment_ids?.[0];
    const target = this.reassignTargetControl.value;
    if (!this.reassignCase || !ghostId || !target) {
      return;
    }
    this.processing = true;
    this.service.reassign(this.reassignCase.event_id, ghostId, target).subscribe({
      next: (res) => {
        this.notifications.success(res.message);
        this.isReassignOpen = false;
        this.reassignCase = null;
        this.afterAction();
      },
      error: (err) => this.handleError(err),
    });
  }

  private afterAction(): void {
    this.processing = false;
    this.selectedRowIds = new Set<number>();
    this.loadData();
  }

  private handleError(err: any): void {
    this.processing = false;
    this.notifications.error(err?.error?.message ?? 'The action could not be completed.');
  }

  protected toggleExplainer(): void {
    this.isExplainerOpen = !this.isExplainerOpen;
  }

  /**
   * TVG-207: open the event's Products & Finances page in a new browser tab so
   * finance can check the real payment detail without leaving the remediation
   * list. Deep-links to the tab via the `tab` query param honoured by
   * {@link EventViewComponent}.
   */
  protected openFinances(row: RemediationCase): void {
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/events', row.event_id], {
        queryParams: { tab: 'financial-summary' },
      }),
    );
    window.open(url, '_blank', 'noopener');
  }

  protected money(value: number): string {
    const sign = value < 0 ? '-' : '';
    return `${sign}£${Math.abs(value).toFixed(2)}`;
  }
}
